import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase, BlogPost, BlogCategory } from '@/lib/supabase';
import { SeoHead } from '@/components/seo/SeoHead';
import { Loader2, Search } from 'lucide-react';
import { HeroPost } from '@/components/blog/HeroPost';
import { StandardPost } from '@/components/blog/StandardPost';
import { SidebarTrending } from '@/components/blog/SidebarTrending';
import { BlogCTA } from '@/components/blog/BlogCTA';

export function BlogPage() {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [trendingPosts, setTrendingPosts] = useState<BlogPost[]>([]);
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  
  const categorySlug = searchParams.get('category');
  const searchQuery = searchParams.get('q') || '';

  useEffect(() => {
    fetchData();
  }, [categorySlug, searchQuery]);

  async function fetchData() {
    setLoading(true);
    try {
      // 1. Categorías
      const { data: cats } = await supabase.from('blog_categories').select('*').order('name');
      if (cats) setCategories(cats);

      // 2. Posts Principales (Feed)
      let query = supabase
        .from('blog_posts')
        .select('*, categories:blog_categories(name, slug)')
        .eq('status', 'published')
        .order('published_at', { ascending: false });

      if (categorySlug) {
        const cat = cats?.find(c => c.slug === categorySlug);
        if (cat) query = query.eq('category_id', cat.id);
      }

      if (searchQuery) {
        query = query.ilike('title', `%${searchQuery}%`);
      }

      const { data: postsData } = await query;
      setPosts(postsData || []);

      // 3. Posts Tendencia (Para el sidebar, ordenados por visitas)
      // Solo si no estamos buscando, para mantener el sidebar relevante
      if (!searchQuery && !categorySlug) {
        const { data: trendingData } = await supabase
            .from('blog_posts')
            .select('*, categories:blog_categories(name, slug)')
            .eq('status', 'published')
            .order('view_count', { ascending: false })
            .limit(5);
        setTrendingPosts(trendingData || []);
      }

    } catch (error) {
      console.error('Error fetching blog:', error);
    } finally {
      setLoading(false);
    }
  }

  // Lógica de distribución de contenido
  const heroPost = posts[0];
  const secondaryHeroes = posts.slice(1, 3);
  const feedPosts = posts.slice(3);

  return (
    <div className="min-h-screen bg-slate-50 pb-20 font-sans">
      <SeoHead 
        title="Revista Inmobiliaria y Trasteros"
        description="Noticias, guías de precios y consejos sobre el mercado de trasteros en España. Información para inversores y particulares."
      />

      {/* Header Revista */}
      <div className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <h1 className="text-2xl font-black tracking-tighter text-slate-900 uppercase">
              EncuentraTrastero <span className="text-blue-600">Magazine</span>
            </h1>
            
            <div className="relative w-full md:w-auto">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input 
                  type="text" 
                  placeholder="Buscar noticias..." 
                  className="w-full md:w-64 pl-9 pr-4 py-2 rounded-full bg-slate-100 border-none text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                  value={searchQuery}
                  onChange={(e) => setSearchParams(prev => {
                    if (e.target.value) prev.set('q', e.target.value);
                    else prev.delete('q');
                    return prev;
                  })}
                />
            </div>
          </div>

          {/* Navegación Categorías */}
          <nav className="flex gap-6 overflow-x-auto py-4 mt-2 no-scrollbar border-t border-slate-100">
            <button
                onClick={() => setSearchParams(prev => { prev.delete('category'); return prev; })}
                className={`text-sm font-bold uppercase tracking-wide whitespace-nowrap transition-colors ${!categorySlug ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : 'text-slate-500 hover:text-slate-900'}`}
            >
                Portada
            </button>
            {categories.map(cat => (
                <button
                key={cat.id}
                onClick={() => setSearchParams(prev => { prev.set('category', cat.slug); return prev; })}
                className={`text-sm font-bold uppercase tracking-wide whitespace-nowrap transition-colors ${categorySlug === cat.slug ? 'text-blue-600 border-b-2 border-blue-600 pb-1' : 'text-slate-500 hover:text-slate-900'}`}
                >
                {cat.name}
                </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
          </div>
        ) : posts.length > 0 ? (
          <>
            {/* 1. SECCIÓN HERO (Solo en portada/sin búsqueda) */}
            {!searchQuery && !categorySlug && (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-12">
                    {/* Hero Principal (2/3) */}
                    <div className="lg:col-span-2">
                        {heroPost && <HeroPost post={heroPost} size="large" />}
                    </div>
                    
                    {/* Top Stories (1/3) */}
                    <div className="flex flex-col gap-4">
                        {secondaryHeroes.map(post => (
                            <div key={post.id} className="flex-1">
                                <HeroPost post={post} size="medium" />
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 2. CUERPO PRINCIPAL (Feed + Sidebar) */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                
                {/* Columna Izquierda: Feed de Noticias (8/12) */}
                <div className="lg:col-span-8 space-y-8">
                    <div className="flex items-center gap-4 mb-6">
                        <h2 className="text-xl font-bold text-slate-900 uppercase tracking-wide border-l-4 border-blue-600 pl-3">
                            {searchQuery ? 'Resultados de búsqueda' : categorySlug ? 'Artículos de la categoría' : 'Últimas Noticias'}
                        </h2>
                        <div className="h-px bg-slate-200 flex-1"></div>
                    </div>

                    {/* Lista de Artículos */}
                    <div className="space-y-6">
                        {(searchQuery || categorySlug ? posts : feedPosts).map((post, index) => (
                            <div key={post.id}>
                                <StandardPost post={post} />
                                {/* Insertar CTA cada 3 artículos */}
                                {(index + 1) % 3 === 0 && (
                                    <div className="my-8">
                                        <BlogCTA type="publish" className="!my-0 !py-6" />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Paginación (Simulada por ahora) */}
                    <div className="pt-12 text-center">
                        <button className="px-8 py-3 bg-white border border-slate-200 text-slate-600 font-bold rounded-full hover:bg-slate-50 transition-colors shadow-sm">
                            Cargar más noticias
                        </button>
                    </div>
                </div>

                {/* Columna Derecha: Sidebar (4/12) */}
                <aside className="lg:col-span-4 space-y-8">
                    {/* Ranking */}
                    <SidebarTrending posts={trendingPosts} />

                    {/* Banner Publicidad Propia (Sticky) */}
                    <div className="sticky top-32">
                        <div className="bg-slate-900 rounded-2xl p-8 text-center text-white shadow-xl relative overflow-hidden">
                            <div className="absolute inset-0 bg-blue-600/20"></div>
                            <div className="relative z-10">
                                <h3 className="text-2xl font-bold mb-2">¿Tienes un trastero?</h3>
                                <p className="text-slate-300 mb-6 text-sm">
                                    Miles de personas buscan espacio en tu zona. Alquila el tuyo gratis.
                                </p>
                                <a 
                                    href="/publish" 
                                    className="block w-full bg-white text-slate-900 font-bold py-3 rounded-xl hover:bg-blue-50 transition-colors"
                                >
                                    Publicar Anuncio
                                </a>
                            </div>
                        </div>

                        {/* Categorías (Nube de tags) */}
                        <div className="mt-8 bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
                            <h3 className="font-bold text-slate-900 mb-4 text-sm uppercase">Temas</h3>
                            <div className="flex flex-wrap gap-2">
                                {categories.map(cat => (
                                    <button
                                        key={cat.id}
                                        onClick={() => setSearchParams(prev => { prev.set('category', cat.slug); return prev; })}
                                        className="px-3 py-1 bg-slate-100 text-slate-600 text-xs font-medium rounded-lg hover:bg-blue-100 hover:text-blue-700 transition-colors"
                                    >
                                        {cat.name}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </aside>

            </div>
          </>
        ) : (
          <div className="text-center py-32">
            <h3 className="text-2xl font-bold text-slate-900 mb-2">No hay artículos disponibles</h3>
            <p className="text-slate-500">Estamos trabajando en nuevo contenido. Vuelve pronto.</p>
          </div>
        )}
      </div>
    </div>
  );
}
